/**
 * Serurubele (Aurum Institute) REDCap Data Transformer
 * 
 * Transforms REDCap export from the Serurubele site into the format
 * required for bulk patient upload.
 * 
 * Key features:
 * - Deduplicates by record_id (multiple visit rows per patient)
 * - Decodes REDCap numeric codes (sex, smoking, HIV, conditions)
 * - Expands checkbox columns (dm_smoking1___1..5, condi_choice___1, etc.)
 * - Generates placeholder emails from Patient Number
 * - Normalizes US-format dates (M/D/YYYY) to ISO YYYY-MM-DD
 */

import { repairPhoneNumber, repairPhoneOrEmpty } from './phoneRepair';


export const SERURUBELE_SITE_ID = '199aaad5-3920-4c10-8bde-16f601d7f86e';
export const SERURUBELE_SITE_NAME = 'Aurum Institute – Serurubele';
export const SERURUBELE_COUNTRY = 'South Africa';

export interface SerurubelePatient {
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  /** Additional number found in the same source cell (household/shared line). */
  secondary_phone?: string;
  date_of_birth?: string;
  medical_record_number: string;
  gender?: 'male' | 'female';
  address?: string;
  location: string;
  smoking_status?: string;
  smoking_detail?: string;
  hiv_status?: string;
  hiv_detail?: string;
  conditions: string[];
  additional_demographics?: Record<string, any>;
}

export interface SerurubeleTransformResult {
  patients: SerurubelePatient[];
  stats: {
    totalRows: number;
    uniquePatients: number;
    duplicatesRemoved: number;
    skippedNoId: number;
    withConditions: number;
    withHiv: number;
    withSmoking: number;
  };
  conditionSummary: Record<string, number>;
}

// ─── Helpers ──────────────────────────────────────────────

function val(row: Record<string, string>, key: string): string {
  return (row[key] ?? '').trim();
}

function flag(row: Record<string, string>, key: string): boolean {
  return val(row, key) === '1';
}

/**
 * Normalize a US-format date (M/D/YYYY) to ISO YYYY-MM-DD.
 * Also handles YYYY-MM-DD passthrough and D/M/YYYY when unambiguous.
 */
function normalizeDate(raw: string): string {
  if (!raw) return '';
  const trimmed = raw.trim();

  // Already ISO
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(trimmed)) {
    const [y, m, d] = trimmed.split('-').map(Number);
    if (y >= 1900 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
    return '';
  }

  // M/D/YYYY (REDCap US export format)
  const parts = trimmed.split('/');
  if (parts.length === 3) {
    const [a, b, c] = parts.map(Number);
    if (isNaN(a) || isNaN(b) || isNaN(c)) return '';
    
    // REDCap exports in M/D/YYYY for this site
    const month = a;
    const day = b;
    const year = c;
    
    if (year >= 1900 && year <= new Date().getFullYear() + 1 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }

  return '';
}

/**
 * Repair + validate a REDCap phone cell for this (South African) site.
 * Delegates to the shared import repairer so malformed values — 0027 prefixes,
 * missing +, duplicated country codes, spreadsheet-dropped leading zeros,
 * extensions, letters and multi-number cells — are corrected when possible
 * instead of being discarded.
 */
function normalizePhone(raw: string): string {
  return repairPhoneOrEmpty(raw, 'ZA');
}

/** Any additional numbers found in the same cell (household/shared lines). */
function extraPhones(raw: string): string[] {
  return repairPhoneNumber(raw, 'ZA').extras;
}


// ─── Deduplication ──────────────────────────────────────────

/**
 * Group rows by record_id, merge taking the most complete value for each field.
 */
function deduplicateByRecordId(rows: Record<string, string>[]): Record<string, string>[] {
  const groups = new Map<string, Record<string, string>>();
  
  for (const row of rows) {
    const id = val(row, 'record_id');
    if (!id) continue;
    
    const existing = groups.get(id);
    if (!existing) {
      groups.set(id, { ...row });
    } else {
      // Merge: keep the non-empty value (prefer existing, fill gaps)
      for (const [key, value] of Object.entries(row)) {
        if (value && value.trim() && (!existing[key] || !existing[key].trim())) {
          existing[key] = value;
        }
      }
    }
  }
  
  return Array.from(groups.values());
}

// ─── Row Decoder ──────────────────────────────────────────

function decodePatient(row: Record<string, string>): SerurubelePatient | null {
  const patientNumber = val(row, 'Patient Number');
  const firstName = val(row, 'reg_name');
  const lastName = val(row, 'reg_surname');
  
  // Must have at least a patient number or name
  if (!patientNumber && !firstName && !lastName) return null;
  
  // Generate placeholder email from patient number
  const emailBase = patientNumber
    ? patientNumber.toLowerCase().replace(/[^a-z0-9\-]/g, '')
    : `${val(row, 'record_id')}`;
  const email = `${emailBase}@serurubele.aurum.redcap.import`;
  
  // Gender: 1=Male, 2=Female
  const sexCode = val(row, 'reg_sex');
  const gender: 'male' | 'female' | undefined = 
    sexCode === '1' ? 'male' : sexCode === '2' ? 'female' : undefined;
  
  // Date of birth (US format M/D/YYYY in this REDCap export)
  const dob = normalizeDate(val(row, 'reg_dob'));
  
  // Phone (repaired where possible; extra numbers in the same cell are kept)
  const rawPhone = val(row, 'reg_phone');
  const phone = normalizePhone(rawPhone);
  const secondaryPhone = extraPhones(rawPhone)[0];

  
  // Address
  const address = val(row, 'locator_primaryaddress');
  
  // ─── Smoking ───
  const dmSmoking = val(row, 'dm_smoking');
  let smokingStatus: string | undefined;
  let smokingDetail = '';
  
  if (dmSmoking === '1') {
    smokingStatus = 'current';
    const types: string[] = [];
    if (flag(row, 'dm_smoking1___1')) types.push('Cigarettes');
    if (flag(row, 'dm_smoking1___2')) types.push('Hubbly');
    if (flag(row, 'dm_smoking1___3')) types.push('Vape');
    if (flag(row, 'dm_smoking1___4')) types.push('Marijuana');
    if (flag(row, 'dm_smoking1___5')) {
      const other = val(row, 'dm_smokeother');
      types.push(other ? `Other (${other})` : 'Other');
    }
    
    if (types.length > 0) smokingDetail = types.join(', ');
    
    // Cigarette frequency
    const cigFreq = val(row, 'dm_smokeciggarette');
    if (cigFreq === '1') smokingDetail += ' (<5/day)';
    else if (cigFreq === '2') smokingDetail += ' (5-10/day)';
    else if (cigFreq === '3') smokingDetail += ' (>10/day)';
  } else if (dmSmoking === '0') {
    smokingStatus = 'never';
  }
  
  // ─── HIV ───
  const hivCode = val(row, 'hiv_status');
  let hivStatus: string | undefined;
  let hivDetail = '';
  
  if (hivCode === '1') {
    hivStatus = 'positive';
    const parts: string[] = [];
    const treatment = val(row, 'hiv_treatment');
    if (treatment) parts.push(`ART: ${treatment}`);
    
    const viralLoad = val(row, 'hiv_viralload');
    if (viralLoad === '1') parts.push('Virally suppressed');
    else if (viralLoad === '2') parts.push('Not suppressed');
    else if (viralLoad === '3') parts.push('Viral load unknown');
    
    const diagDate = val(row, 'hiv_datediagnosis');
    if (diagDate) parts.push(`Diagnosed: ${diagDate}`);
    
    const treatStart = val(row, 'hiv_treatmentstart');
    if (treatStart) parts.push(`Treatment started: ${treatStart}`);
    
    hivDetail = parts.join('; ');
  } else if (hivCode === '2') {
    hivStatus = 'negative';
    const lastTest = val(row, 'hiv_dateoflasttest');
    if (lastTest) hivDetail = `Last tested: ${lastTest}`;
  } else if (hivCode === '3') {
    hivStatus = 'unknown';
    const neverTested = val(row, 'hiv_nevertested');
    if (neverTested === '1') hivDetail = 'Never tested';
  }
  
  // ─── Medical Conditions ───
  const conditions: string[] = [];
  
  if (flag(row, 'condi_choice___1')) conditions.push('Hypertension');
  if (flag(row, 'condi_choice1___1')) conditions.push('Diabetes');
  if (flag(row, 'condi_choice2___1')) conditions.push('Epilepsy');
  if (flag(row, 'condi_choice3___1')) conditions.push('Cardiovascular Disease');
  if (flag(row, 'condi_choice4___1')) conditions.push('Hepatitis');
  if (flag(row, 'condi_choice5___1')) {
    const other = val(row, 'mh_other');
    conditions.push(other || 'Other condition');
  }
  
  // TB
  if (flag(row, 'mh_tb') || val(row, 'mh_tb') === '1') {
    const tbTypes: string[] = [];
    if (flag(row, 'mh_typeoftb___1')) tbTypes.push('Pulmonary');
    if (flag(row, 'mh_typeoftb___2')) tbTypes.push('Brain');
    if (flag(row, 'mh_typeoftb___3')) tbTypes.push('Abdominal');
    if (flag(row, 'mh_typeoftb___4')) tbTypes.push('Glandular');
    if (flag(row, 'mh_typeoftb___5')) tbTypes.push('Disseminated');
    const tbLabel = tbTypes.length > 0 ? `Tuberculosis (${tbTypes.join(', ')})` : 'Tuberculosis';
    conditions.push(tbLabel);
  }
  
  // ─── Additional Demographics ───
  const additionalDemographics: Record<string, any> = {};
  
  const community = val(row, 'community_nickname');
  if (community) additionalDemographics.community_name = community;
  
  const bpSys = val(row, 'mh_bpsystolic');
  const bpDia = val(row, 'mh_bpdiastolic');
  if (bpSys && bpDia) additionalDemographics.blood_pressure = `${bpSys}/${bpDia}`;
  
  const meds = val(row, 'mh_listmeds');
  if (meds) additionalDemographics.current_medications = meds;
  
  const eligibility = val(row, 'eligibility_decision');
  if (eligibility === '1') additionalDemographics.eligibility = 'Eligible';
  else if (eligibility === '2') additionalDemographics.eligibility = 'Eligible with conditions';
  else if (eligibility === '3') additionalDemographics.eligibility = 'Ineligible';
  
  return {
    email,
    first_name: firstName,
    last_name: lastName,
    phone: phone || undefined,
    secondary_phone: secondaryPhone || undefined,
    date_of_birth: dob || undefined,
    medical_record_number: patientNumber,
    gender,
    address: address || undefined,
    location: SERURUBELE_COUNTRY,
    smoking_status: smokingStatus,
    smoking_detail: smokingDetail || undefined,
    hiv_status: hivStatus,
    hiv_detail: hivDetail || undefined,
    conditions,
    additional_demographics: Object.keys(additionalDemographics).length > 0 ? additionalDemographics : undefined,
  };
}

// ─── Main Transform ──────────────────────────────────────────

export function transformSerurubelePatients(
  rawRows: Record<string, string>[]
): SerurubeleTransformResult {
  const totalRows = rawRows.length;
  
  // Deduplicate by record_id
  const deduplicated = deduplicateByRecordId(rawRows);
  const duplicatesRemoved = totalRows - deduplicated.length;
  
  const patients: SerurubelePatient[] = [];
  let skippedNoId = 0;
  const conditionSummary: Record<string, number> = {};
  
  for (const row of deduplicated) {
    const patient = decodePatient(row);
    if (!patient) {
      skippedNoId++;
      continue;
    }
    
    patients.push(patient);
    
    for (const cond of patient.conditions) {
      conditionSummary[cond] = (conditionSummary[cond] || 0) + 1;
    }
  }
  
  return {
    patients,
    stats: {
      totalRows,
      uniquePatients: patients.length,
      duplicatesRemoved,
      skippedNoId,
      withConditions: patients.filter(p => p.conditions.length > 0).length,
      withHiv: patients.filter(p => p.hiv_status).length,
      withSmoking: patients.filter(p => p.smoking_status).length,
    },
    conditionSummary,
  };
}
